import type { Coupon, Outcome } from '../../../../schema/coupon/entry.js';
import { NO_REDUCTION, scoreFilterFnMap } from '../coupon/reduction.js';

function getBinaryNumbers(decimalValue: number): number[] {
  const binaryNumbers = [];
  let currentDecimalValue = decimalValue;
  let i = 0;

  while (currentDecimalValue !== 0) {
    if (currentDecimalValue & 1) {
      binaryNumbers.push(2 ** i);
    }
    currentDecimalValue >>= 1;
    i++;
  }

  return binaryNumbers;
}

/**
 * Computes all cartesian products within each event row.
 *
 * @param outcomeGroups Array of outcome groups for a single event
 * @returns Array of all possible combinations within the event row
 * @example
 * // [[a,b], [c]] -> [[a,c], [b,c]]
 */
export function getCartesianProducts(outcomeGroups: Coupon['boards'][number]['outcomes'][number]): Outcome[][] {
  return outcomeGroups.reduce<Outcome[][]>(
    (acc, outcomeGroup) => acc.flatMap((prefix) => outcomeGroup.map((outcome) => [...prefix, outcome])),
    [[]]
  );
}

/**
 * Computes filtered cartesian products for each event in a board based on reduction values.
 *
 * @param board The coupon board containing event outcomes and reduction info
 * @param engineReductionSum The reduction sum used for filtering
 * @returns An array of filtered outcome combinations for each event
 */
export function getBoardCartesianProducts(board: Coupon['boards'][number], engineReductionSum: number): Outcome[][][] {
  return board.outcomes.map((eventOutcomes, eventIndex) => {
    const eventReduction = board.reduction?.[eventIndex] ?? NO_REDUCTION;
    const isEventReduced = engineReductionSum > 0 && eventReduction !== engineReductionSum;

    if (!isEventReduced) {
      return getCartesianProducts(eventOutcomes);
    }

    const reductionArray = getBinaryNumbers(eventReduction);
    const filterFns = reductionArray.map((reductionValue) => scoreFilterFnMap[reductionValue]);
    const eventCombinations = getCartesianProducts(eventOutcomes);

    return eventCombinations.filter((outcomePair) => filterFns.some((filter) => filter?.(...outcomePair)));
  });
}
